import { gql } from "@apollo/client/core";
import { onMounted, onUnmounted } from "vue";
import { apolloClient } from "@/graphql/apollo";
import {
  activeDynamicTranslationCatalogVersion,
  refreshActiveDynamicTranslationCatalog,
} from "./dynamicTranslations";

const dynamicTranslationCatalogChanged = gql`
  subscription DynamicTranslationCatalogChanged {
    DynamicTranslationCatalogChanged
  }
`;

type DynamicTranslationCatalogChangedResult = {
  DynamicTranslationCatalogChanged: string;
};

export function useDynamicTranslationCatalogSync() {
  async function refresh() {
    try {
      await refreshActiveDynamicTranslationCatalog();
    } catch (error) {
      // Keep the last valid catalog. A later subscription or tab focus retries the revalidation.
      // eslint-disable-next-line no-console
      console.error("Failed to refresh the dynamic translation catalog", error);
    }
  }

  function refreshWhenVisible() {
    if (document.visibilityState === "visible") void refresh();
  }

  let unsubscribe: (() => void) | undefined;
  onMounted(() => {
    const subscription = apolloClient
      .subscribe<DynamicTranslationCatalogChangedResult>({
        query: dynamicTranslationCatalogChanged,
      })
      .subscribe({
        next(result) {
          const version = result.data?.DynamicTranslationCatalogChanged;
          if (version != null && version !== activeDynamicTranslationCatalogVersion.value) {
            void refresh();
          }
        },
        error(error) {
          // graphql-ws reconnects automatically; visibility changes provide an eventual retry.
          // eslint-disable-next-line no-console
          console.error("Dynamic translation catalog subscription failed", error);
        },
      });
    unsubscribe = () => subscription.unsubscribe();
    document.addEventListener("visibilitychange", refreshWhenVisible);
  });
  onUnmounted(() => {
    unsubscribe?.();
    document.removeEventListener("visibilitychange", refreshWhenVisible);
  });
}
